import { Component, Emit, Ref, Vue, Watch } from 'vue-property-decorator'
import SearcherModule from '@/components/SearcherModule.vue'
import ModalConfirmation from '@/components/modals/ModalConfirmation.vue'
import { shipmentsItemFinderService } from '@/modules/shipments-module/services/item/item-finder/shipments-item-finder.service'
import { shipmentsRequestFiltererService } from '@/modules/shipments-module/services/request/request-filterer/shipments-request-filterer.service'
import { IdentifierTypeScope } from '@/api/resources/common/initialization/common-initialization.dto'
import {
    ShipmentItemResponse,
    ShipmentRequestAndItemResponse,
    ShipmentRequestResponse
} from '@/modules/shipments-module/services/shipments.dto'
import { StrictNullable } from '@/shared/types/app.types'
import {
    getSelectedOptionIdFromShipmentStatus,
    ShipmentsSearch,
    ShipmentsSearcherResponse
} from '@/modules/shipments-module/components/searcher/shipments-searcher.model'
import apiExceptionType from '@/utils/apiExceptionType'
import { findNodeByPropertyValue } from '@/components/sideBar/sideBarUtils'
import { shipmentsCompleteArray } from '@/modules/shipments-module/components/sidebar/shipments-sidebar.model'
import { store } from '@/store/store'
import { ShipmentsStoreHelper } from '@/modules/shipments-module/store/shipments-store.helper'
import { ModalUtils } from '@/shared/util/modal-utils'
import { PromiseUtils } from '@/shared/util/promise-utils'

@Component({
    components: {
        SearcherModule,
        ModalConfirmation
    }
})
export default class ShipmentsSearcher extends Vue {
    @Ref() readonly searcherModule!: any
    @Ref() readonly confirmationModal!: any

    @Watch('sidebarSelectedOptionId')
    private _onSidebarSelectedOptionChanged(): void {
        this.searcherModule.focusInput()
    }

    @Emit()
    found(entitiesSelected: ShipmentsSearcherResponse[]): ShipmentsSearcherResponse[] {
        return entitiesSelected
    }

    get identifierScopes(): typeof IdentifierTypeScope {
        return IdentifierTypeScope
    }

    private _getEntityNotFoundError(entityType?: string): string {
        switch (entityType) {
        case IdentifierTypeScope.SAMPLE:
            return this.$tc('component.searcher.error.noSample')
        case IdentifierTypeScope.PORTION:
            return this.$tc('component.searcher.error.noPortion')
        case IdentifierTypeScope.SUBPORTION:
            return this.$tc('component.searcher.error.noSubportion')
        default:
            return this.$tc('component.searcher.error.validIdentifier')
        }
    }

    private static _parseSearchEntityValueToSample(shipmentRequest: ShipmentRequestResponse) {
        return {
            id: shipmentRequest.id,
            sampleLabel: shipmentRequest.sample.sampleLabel,
            sampleType: shipmentRequest.sample.sampleType,
            topographicCode: shipmentRequest.sample.topographicCode,
            caseStudy: {
                casenumber: shipmentRequest.casenumber,
                laboratory: shipmentRequest.destinationLaboratory, // TODO
                patient: shipmentRequest.patient
            }
        }
    }
    private static _parseSearchEntityValueToPortion(shipmentRequest: ShipmentRequestResponse) {
        return {
            id: shipmentRequest.id,
            portionLabel: shipmentRequest.portion.portionLabel,
            portionType: shipmentRequest.portion.portionType,
            sample: {
                sampleLabel: shipmentRequest.sample.sampleLabel,
                caseStudy: {
                    casenumber: shipmentRequest.casenumber,
                    laboratory: shipmentRequest.destinationLaboratory,
                    patient: shipmentRequest.patient
                }
            }
        }
    }
    private static _parseSearchEntityValueToSubportion(shipmentRequest: ShipmentRequestResponse) {
        return {
            id: shipmentRequest.id,
            subportionLabel: shipmentRequest.subportion.subportionLabel,
            portion: {
                portionLabel: shipmentRequest.portion.portionLabel,
                sample: {
                    sampleLabel: shipmentRequest.sample.sampleLabel,
                    caseStudy: {
                        casenumber: shipmentRequest.casenumber,
                        laboratory: shipmentRequest.destinationLaboratory,
                        patient: shipmentRequest.patient
                    }
                }
            },
            assay: shipmentRequest.subportion.assay
        }
    }
    private static _parseSearchEntityValue(shipmentRequest: ShipmentRequestResponse, entityType: IdentifierTypeScope) {
        switch (entityType) {
        case IdentifierTypeScope.SAMPLE:
            return ShipmentsSearcher._parseSearchEntityValueToSample(shipmentRequest)
        case IdentifierTypeScope.PORTION:
            return ShipmentsSearcher._parseSearchEntityValueToPortion(shipmentRequest)
        case IdentifierTypeScope.SUBPORTION:
            return ShipmentsSearcher._parseSearchEntityValueToSubportion(shipmentRequest)
        default:
            return null
        }
    }

    /// SERVICES CALLS ///

    private async _findByDeliveryNote(
        laboratoryId: number,
        deliveryNote: string
    ): Promise<ShipmentItemResponse[] | null> {
        const response = await PromiseUtils.toCallUnique(
            shipmentsItemFinderService.findByLabel(laboratoryId, deliveryNote),
            error => ModalUtils.showErrorModal(
                error.response.data.code === apiExceptionType.ENTITY_NOT_FOUND
                    ? this.$tc('component.searcher.error.noDeliveryNote')
                    : undefined))
        return response?.content ?? null
    }

    private _findByEntityLabel(
        laboratoryId: number,
        label: string,
        entityType?: string
    ): Promise<ShipmentRequestAndItemResponse[] | null> {
        return PromiseUtils.toCallUnique(
            shipmentsRequestFiltererService.findByLabel(laboratoryId, label),
            error => ModalUtils.showErrorModal(
                error.response.data.code === apiExceptionType.ENTITY_NOT_FOUND
                    ? this._getEntityNotFoundError(entityType)
                    : undefined))
    }

    ///

    private async _saveSearch(
        sidebarSelectedOption: any,
        entitiesSelected: ShipmentsSearcherResponse[]
    ): Promise<void> {
        if (sidebarSelectedOption) {
            await ShipmentsStoreHelper.setSidebarSelectedOption(sidebarSelectedOption)
            this.found(entitiesSelected) // emits an event
        } else {
            ModalUtils.showErrorModal()
        }
    }

    private async _onEntitySearch(searchInformation: any, entityType: IdentifierTypeScope): Promise<ShipmentsSearch> {
        let sidebarSelectedOptionId: StrictNullable<string> = null
        let entitiesSelected: ShipmentsSearcherResponse[] = []

        const response = await this._findByEntityLabel(store.state.labId, searchInformation.search, entityType)
        if (response?.length) {
            let entitySelected = response[0]
            if (response.length > 1) {
                const foundEntities =
                    response.map(entity => ({
                        entityType: entityType,
                        entityValue: ShipmentsSearcher._parseSearchEntityValue(entity.shipmentRequestResponse, entityType),
                        laboratoryId: entity.shipmentRequestResponse.destinationLaboratory
                    }))
                const entityValueSelected =
                    await this.$_showMultiEntitySelection(searchInformation.search, foundEntities)
                entitySelected =
                    response.find(entity => entity.shipmentRequestResponse.id === entityValueSelected.id)!
            }
            entitiesSelected = [entitySelected]
            sidebarSelectedOptionId =
                getSelectedOptionIdFromShipmentStatus(
                    entitySelected.shipmentRequestResponse,
                    entitySelected.shipmentItemResponse)
        }
        return { entitiesSelected, sidebarSelectedOptionId }
    }
    private async _deliveryNoteSearch(searchInformation: any): Promise<ShipmentsSearch> {
        const entitiesSelected =
            await this._findByDeliveryNote(store.state.labId, searchInformation.search) ?? []
        const sidebarSelectedOptionId =
            entitiesSelected.length
                ? getSelectedOptionIdFromShipmentStatus(entitiesSelected[0].shipmentRequest, entitiesSelected[0])
                : null
        return { entitiesSelected, sidebarSelectedOptionId }
    }
    async onSearch(searchInformation: any, entityType: IdentifierTypeScope): Promise<void> {
        let shipmentSearch: StrictNullable<ShipmentsSearch> = null
        if (entityType === IdentifierTypeScope.SAMPLE ||
                entityType === IdentifierTypeScope.PORTION ||
                entityType === IdentifierTypeScope.SUBPORTION) {
            shipmentSearch = await this._onEntitySearch(searchInformation, entityType)
        } else if (entityType === IdentifierTypeScope.SHIPMENT) {
            shipmentSearch = await this._deliveryNoteSearch(searchInformation)
        }

        if (shipmentSearch) {
            const { entitiesSelected, sidebarSelectedOptionId } = shipmentSearch
            if (sidebarSelectedOptionId && entitiesSelected.length) {
                if (sidebarSelectedOptionId === ShipmentsStoreHelper.sidebarSelectedOption?.id) {
                    await this._saveSearch(ShipmentsStoreHelper.sidebarSelectedOption, entitiesSelected)
                } else {
                    const sidebarSelectedOption =
                        findNodeByPropertyValue('id', sidebarSelectedOptionId, shipmentsCompleteArray)
                    this.confirmationModal.ask(() => this._saveSearch(sidebarSelectedOption, entitiesSelected))
                }
            }
        }
    }
}
